/* aquamarine-set.js — Step One · Aquamarine 5-Piece Set (standalone women's page)
 *
 * Forked from yacht-fleet-drop.js. Ported near-verbatim: the GA4 dataLayer helper, the
 * tall-surface-safe IntersectionObserver, the _s1.addToCartAndRefreshCart readiness
 * guard, the per-tile quick-add wiring, the gallery swipe/dots, and the builder's tier
 * math/recalc loop. Removed: everything to do with Chapter 2 and the Group A/B split —
 * Aquamarine's 5 pieces are 5 different garment types, not two buckets of the same
 * garment (see AQUAMARINE-HANDOFF.md §2). Added: a size-guide modal opener.
 *
 * IMPORTANT: this file NEVER pushes add_to_cart / remove_from_cart / begin_checkout /
 * purchase — Analyzify's Customer Events pixel already emits those from the real
 * /cart/add.js call. Duplicating them double-counts revenue.
 *
 * Scoped entirely to elements inside .aqs-section.
 */
(function () {
  'use strict';

  /* ────────────────────────────────────────────────────────────────
   * Size guide data — real Step One measurement charts (verified
   * 18/08/2026 against 5 live "Find Your Perfect Fit" screenshots, not
   * placeholder copy). Two garment shapes cover all 5 Aquamarine pieces:
   * the Bra uses its own band+cup chart (chart-only, no calculator — a
   * band+cup size doesn't reduce to a single waist/hip measurement the
   * way a brief style does); Bikini Brief, Full Brief, Thong and Boyleg
   * all share this identical waist+hip chart and get a two-input
   * calculator, porting the range-match + "larger size wins on a tie"
   * logic from s1-pdp.js's S1SizeModal.calculate(). That file has no
   * women's underwear data at all (checked — only Men's Boxer Brief,
   * Sleepwear, Compression, Socks, XCup, Junior, Girls Shortie), so this
   * is a self-contained port scoped to this section, not a shared call
   * into s1-pdp.js. Worth the developer consolidating later if wanted.
   * ──────────────────────────────────────────────────────────────── */
  var WOMENS_BRA_DATA = [
    { size: 'XS', auNz: '8A, 8B, 10A', uk: '30A, 30B, 32A', us: '30A, 30B, 32A' },
    { size: 'S', auNz: '8DD, 10B, 10C, 10D, 10DD, 12A, 12B', uk: '30DD, 32B, 32C, 32D, 32DD, 34A, 34B', us: '30DD, 32B, 32DD, 32C, 32D, 34A, 34B' },
    { size: 'M', auNz: '12C, 12D, 12DD, 12E, 14A, 14B', uk: '34C, 34D, 34DD, 34E, 36A, 36B', us: '34C, 34D, 34DD, 34DDD, 36A, 36B' },
    { size: 'L', auNz: '14C, 14D, 14DD, 14E, 16A, 16B, 16C', uk: '36C, 36D, 36DD, 36E, 38A, 38B, 38C', us: '36C, 36D, 36DD, 36DDD, 38B, 38C' },
    { size: 'XL', auNz: '16D, 16DD, 16E, 18A, 18B, 18C, 18D', uk: '38D, 38DD, 38E, 40A, 40B, 40C, 40D', us: '38D, 38DD, 38DDD, 40A, 40B, 40C, 40D' },
  ];

  var WOMENS_BRIEF_DATA = [
    { size: 'XS', waistCm: [60, 65], waistIn: [23.5, 25.5], hipCm: [82, 87], hipIn: [32.5, 34.5] },
    { size: 'S', waistCm: [65, 71], waistIn: [25.5, 28], hipCm: [87, 93], hipIn: [34.5, 36.5] },
    { size: 'M', waistCm: [71, 76], waistIn: [28, 30], hipCm: [93, 98], hipIn: [36.5, 38.5] },
    { size: 'L', waistCm: [76, 82], waistIn: [30, 32.5], hipCm: [98, 104], hipIn: [38.5, 41] },
    { size: 'XL', waistCm: [82, 87], waistIn: [32.5, 34.5], hipCm: [104, 109], hipIn: [41, 43] },
    { size: '2XL', waistCm: [87, 93], waistIn: [34.5, 36.5], hipCm: [109, 115], hipIn: [43, 45.5] },
  ];

  // Recommended-size shorthand (S/M/L) -> the actual option label rendered
  // by the section's per-item <select> (Shopify variant option values are
  // "Small"/"Medium"/"Large" but "XS"/"XL"/"2XL" un-abbreviated) — same
  // mismatch s1-pdp.js's SIZE_ALIAS table exists to bridge.
  var SIZE_LABEL_ALIAS = { S: 'Small', M: 'Medium', L: 'Large' };

  /* ────────────────────────────────────────────────────────────────
   * 0. Smooth-scroll anchor nav
   * ──────────────────────────────────────────────────────────────── */
  document.querySelectorAll('a[href^="#aqs-"]').forEach(function (a) {
    a.addEventListener('click', function (e) {
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  /* ────────────────────────────────────────────────────────────────
   * 1. GA4 dataLayer helper
   * ──────────────────────────────────────────────────────────────── */
  function gaPush(eventName, ecommerce, source) {
    var dl = (window.dataLayer = window.dataLayer || []);
    dl.push({ ecommerce: null }); // clear prior ecommerce object (GTM hygiene)
    dl.push({
      event: eventName,
      analyzify_source: source || 'aquamarine-set',
      ecommerce: ecommerce,
    });
  }

  /* Tall-surface-safe IntersectionObserver, per §4d of the GA4 handoff spec:
   * fires when EITHER 50% of the element is visible OR the element covers
   * 50% of the viewport (long grids/sections never reach 0.5 ratio otherwise). */
  function observeOnce(el, key, seenSet, cb) {
    if (!el || seenSet.has(key)) return;
    if (!('IntersectionObserver' in window)) {
      seenSet.add(key);
      cb();
      return;
    }
    var thresh = [];
    for (var i = 0; i <= 20; i++) thresh.push(i / 20);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (seenSet.has(key)) return;
        var vp = window.innerHeight || document.documentElement.clientHeight || 0;
        var coversHalfViewport = entry.intersectionRect.height >= vp * 0.5;
        if (entry.intersectionRatio < 0.5 && !coversHalfViewport) return;
        seenSet.add(key);
        cb();
        io.disconnect();
      });
    }, { threshold: thresh });

    io.observe(el);
  }

  document.querySelectorAll('.aqs-section').forEach(function (section) {
    var sectionId = section.id || 'aqs-section';
    var gaRegion = section.getAttribute('data-ga-region') || 'AU';
    var seen = new Set();

    function itemId(productId, variantId) {
      return 'shopify_' + gaRegion + '_' + productId + '_' + variantId;
    }

    /* Same readiness guard as yacht-fleet-drop.js: an unguarded
     * _s1.addToCartAndRefreshCart(...) call throws synchronously if _s1 isn't
     * defined yet, leaving a button stuck on "Adding…" forever with nothing
     * in the cart and no visible error. Every _s1 call in this file routes
     * through this guard. */
    function whenS1Ready(methodName, cb, onTimeout) {
      function ready() {
        return window._s1 && typeof window._s1[methodName] === 'function';
      }
      if (ready()) { cb(window._s1); return; }
      var tries = 0;
      var iv = setInterval(function () {
        if (ready()) { clearInterval(iv); cb(window._s1); }
        else if (++tries > 40) {  // ~10s, then give up
          clearInterval(iv);
          console.warn('[Aquamarine Set] _s1.' + methodName + ' never became available.');
          if (typeof onTimeout === 'function') onTimeout();
        }
      }, 250);
    }

    /* ── 2. Product list impressions + selects (the 5-piece grid) ── */
    section.querySelectorAll('[data-ga-list-id]').forEach(function (listEl) {
      var listId = listEl.getAttribute('data-ga-list-id');
      var listName = listEl.getAttribute('data-ga-list-name') || listId;
      var cards = listEl.querySelectorAll('[data-ga-card]');

      observeOnce(listEl, sectionId + ':' + listId, seen, function () {
        var items = [];
        cards.forEach(function (card) {
          items.push({
            item_id: card.getAttribute('data-ga-id'),
            item_name: card.getAttribute('data-ga-name'),
            price: parseFloat(card.getAttribute('data-ga-price')) || 0,
            index: parseInt(card.getAttribute('data-ga-index'), 10) || 0,
            item_list_id: listId,
            item_list_name: listName,
          });
        });
        if (!items.length) return;
        gaPush('ee_view_item_list', { item_list_id: listId, item_list_name: listName, items: items });
      });

      listEl.addEventListener('click', function (e) {
        var card = e.target.closest('[data-ga-card]');
        if (!card || !listEl.contains(card)) return;
        gaPush('ee_select_item', {
          item_list_id: listId,
          item_list_name: listName,
          items: [{
            item_id: card.getAttribute('data-ga-id'),
            item_name: card.getAttribute('data-ga-name'),
            price: parseFloat(card.getAttribute('data-ga-price')) || 0,
            index: parseInt(card.getAttribute('data-ga-index'), 10) || 0,
            item_list_id: listId,
            item_list_name: listName,
          }],
        });
        // No preventDefault(): GA4 tags use sendBeacon and survive navigation.
      });
    });

    /* ── 2b. Product card image galleries (swipe/scroll + dot nav) ── */
    section.querySelectorAll('[data-aqs-gallery]').forEach(function (gallery) {
      var dotsWrap = gallery.parentElement.querySelector('[data-aqs-dots]');
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('button');
      if (!dots.length) return;

      function setActive(index) {
        dots.forEach(function (d, i) {
          if (i === index) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          gallery.scrollTo({ left: gallery.clientWidth * i, behavior: 'smooth' });
          setActive(i);
        });
      });

      var scrollTimeout;
      gallery.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var slideWidth = gallery.clientWidth || 1;
          setActive(Math.round(gallery.scrollLeft / slideWidth));
        }, 80);
      }, { passive: true });
    });

    /* ── 2c. Per-tile quick-add (size selector + Add to Cart on each card) ──
     * Each widget's <select> options ARE variant IDs directly (rendered
     * server-side per product), so no client-side variant lookup is needed. */
    section.querySelectorAll('[data-aqs-quick-add]').forEach(function (widget) {
      var select = widget.querySelector('[data-aqs-quick-size]');
      var btn = widget.querySelector('[data-aqs-quick-add-btn]');
      var quickStatusEl = widget.querySelector('[data-aqs-quick-status]');
      if (!select || !btn) return;

      select.addEventListener('click', function (e) { e.stopPropagation(); });
      select.addEventListener('change', function (e) { e.stopPropagation(); });

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var variantId = select.value;
        if (!variantId) return;

        var original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        if (quickStatusEl) quickStatusEl.textContent = '';

        function onSuccess() {
          btn.textContent = '✓ Added';
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 1800);
        }

        function onError(err) {
          console.error('[Aquamarine Set] Quick-add error:', err);
          btn.textContent = original;
          btn.disabled = false;
          if (quickStatusEl) quickStatusEl.textContent = 'Something went wrong — please try again.';
        }

        // NOTE: does NOT push add_to_cart to GA4 — Analyzify already emits it
        // from the real /cart/add.js call triggered by addToCartAndRefreshCart.
        whenS1Ready('addToCartAndRefreshCart', function (s1) {
          var items = [{ id: parseInt(variantId, 10), quantity: 1 }];
          var result = s1.addToCartAndRefreshCart(items);
          if (result && typeof result.then === 'function') {
            result.then(onSuccess).catch(onError);
          } else {
            onSuccess();
          }
        }, function () {
          onError(new Error('_s1.addToCartAndRefreshCart never became available'));
        });
      });
    });

    /* ── 2d. Size guide modal — opened from any card's "Size Guide" link.
     * One shared modal per section; each open reconfigures it (chart data,
     * calculator visibility, and which card's <select> "Select Size"
     * should write into) from the triggering button's data attributes. ── */
    var sizeGuideOverlay = section.querySelector('[data-aqs-sizeguide-overlay]');
    if (sizeGuideOverlay) {
      var sgClose = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-close]');
      var sgTabs = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-tabs]');
      var sgCalcView = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-calculator]');
      var sgChartView = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-chart]');
      var sgChartHead = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-chart-head]');
      var sgChartBody = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-chart-body]');
      var sgWaist = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-waist]');
      var sgHip = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-hip]');
      var sgUnitLabels = sizeGuideOverlay.querySelectorAll('[data-aqs-sizeguide-unit-label]');
      var sgCalcBtn = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-calculate]');
      var sgForm = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-form]');
      var sgResult = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-result]');
      var sgResultSize = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-result-size]');
      var sgSelectSizeLabel = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-select-size]');
      var sgResetBtn = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-reset]');
      var sgSelectBtn = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-select]');
      var sgBackBtn = sizeGuideOverlay.querySelector('[data-aqs-sizeguide-back]');

      var sgState = { type: 'brief', unit: 'cm', targetId: null, recommendation: null };

      function sgData() { return sgState.type === 'bra' ? WOMENS_BRA_DATA : WOMENS_BRIEF_DATA; }

      function sgBuildChart() {
        var data = sgData();
        if (sgState.type === 'bra') {
          if (sgChartHead) sgChartHead.innerHTML = ['Size', 'AU/NZ', 'UK', 'US'].map(function (c) { return '<th>' + c + '</th>'; }).join('');
          if (sgChartBody) sgChartBody.innerHTML = data.map(function (d) {
            return '<tr><td class="aqs-sizeguide__td-size">' + d.size + '</td><td>' + d.auNz + '</td><td>' + d.uk + '</td><td>' + d.us + '</td></tr>';
          }).join('');
        } else {
          if (sgChartHead) sgChartHead.innerHTML = ['Size', 'Waist (CM)', 'Waist (IN)', 'Hip (CM)', 'Hip (IN)'].map(function (c) { return '<th>' + c + '</th>'; }).join('');
          if (sgChartBody) sgChartBody.innerHTML = data.map(function (d) {
            return '<tr><td class="aqs-sizeguide__td-size">' + d.size + '</td><td>' + d.waistCm[0] + '–' + d.waistCm[1] + '</td><td>' + d.waistIn[0] + '–' + d.waistIn[1] + '</td><td>' + d.hipCm[0] + '–' + d.hipCm[1] + '</td><td>' + d.hipIn[0] + '–' + d.hipIn[1] + '</td></tr>';
          }).join('');
        }
      }

      function sgSetView(view) {
        if (sgCalcView) sgCalcView.hidden = view !== 'calculator';
        if (sgChartView) sgChartView.hidden = view !== 'chart';
        if (sgTabs) sgTabs.querySelectorAll('[data-aqs-sizeguide-view]').forEach(function (btn) {
          btn.classList.toggle('is-active', btn.getAttribute('data-aqs-sizeguide-view') === view);
        });
      }

      function sgSetUnit(unit) {
        sgState.unit = unit;
        sizeGuideOverlay.querySelectorAll('[data-aqs-sizeguide-unit]').forEach(function (btn) {
          btn.classList.toggle('is-active', btn.getAttribute('data-aqs-sizeguide-unit') === unit);
        });
        sgUnitLabels.forEach(function (l) { l.textContent = unit; });
        if (sgWaist) sgWaist.placeholder = unit === 'cm' ? 'e.g. 75' : 'e.g. 29';
        if (sgHip) sgHip.placeholder = unit === 'cm' ? 'e.g. 95' : 'e.g. 37';
      }

      function sgReset() {
        sgState.recommendation = null;
        if (sgWaist) sgWaist.value = '';
        if (sgHip) sgHip.value = '';
        if (sgCalcBtn) sgCalcBtn.disabled = true;
        if (sgForm) sgForm.hidden = false;
        if (sgResult) sgResult.hidden = true;
      }

      // Range-match with a "larger size wins" tie-break when waist and hip
      // point to different sizes — same rule as s1-pdp.js's calculate(),
      // ported here since that file has no women's brief data to call into.
      function sgCalculate() {
        var w = parseFloat(sgWaist ? sgWaist.value : '');
        var h = parseFloat(sgHip ? sgHip.value : '');
        if (!w && !h) return;
        var data = sgData();
        var waistKey = sgState.unit === 'cm' ? 'waistCm' : 'waistIn';
        var hipKey = sgState.unit === 'cm' ? 'hipCm' : 'hipIn';

        function findSize(key, val) {
          if (!val) return null;
          var match = data.filter(function (d) { return val >= d[key][0] && val <= d[key][1]; })[0];
          if (match) return match.size;
          if (val > data[data.length - 1][key][1]) return data[data.length - 1].size;
          if (val < data[0][key][0]) return data[0].size;
          return null;
        }

        var waistSize = findSize(waistKey, w);
        var hipSize = findSize(hipKey, h);
        var finalSize = waistSize || hipSize;
        if (waistSize && hipSize && waistSize !== hipSize) {
          var sizeOrder = data.map(function (d) { return d.size; });
          var wi = sizeOrder.indexOf(waistSize);
          var hi = sizeOrder.indexOf(hipSize);
          finalSize = wi > hi ? waistSize : hipSize;
        }
        sgState.recommendation = finalSize || 'M';
        if (sgResultSize) sgResultSize.textContent = sgState.recommendation;
        if (sgSelectSizeLabel) sgSelectSizeLabel.textContent = sgState.recommendation;
        if (sgForm) sgForm.hidden = true;
        if (sgResult) sgResult.hidden = false;
      }

      function sgApplySelection() {
        if (!sgState.recommendation || !sgState.targetId) return;
        var select = document.getElementById(sgState.targetId);
        if (!select) return;
        var label = SIZE_LABEL_ALIAS[sgState.recommendation] || sgState.recommendation;
        var options = Array.prototype.slice.call(select.querySelectorAll('option'));
        var match = options.filter(function (opt) {
          return !opt.disabled && opt.textContent.replace(/\s*—\s*Sold out\s*$/, '').trim() === label;
        })[0];
        if (match) {
          select.value = match.value;
          select.dispatchEvent(new Event('change'));
        }
      }

      function openSizeGuide(btn) {
        sgState.type = (btn && btn.getAttribute('data-aqs-sizeguide-type')) || 'brief';
        sgState.targetId = btn ? btn.getAttribute('data-aqs-sizeguide-target') : null;
        var chartOnly = sgState.type === 'bra';
        if (sgTabs) sgTabs.hidden = chartOnly;
        if (sgCalcView) sgCalcView.hidden = chartOnly;
        if (sgChartView) sgChartView.hidden = false;
        if (sgBackBtn) sgBackBtn.hidden = chartOnly;
        sgBuildChart();
        sgReset();
        sgSetView(chartOnly ? 'chart' : 'calculator');
        sgSetUnit('cm');
        sizeGuideOverlay.setAttribute('data-open', 'true');
      }
      function closeSizeGuide() { sizeGuideOverlay.setAttribute('data-open', 'false'); }

      section.querySelectorAll('[data-aqs-sizeguide-open]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          e.preventDefault();
          openSizeGuide(btn);
        });
      });
      if (sgClose) sgClose.addEventListener('click', closeSizeGuide);
      sizeGuideOverlay.addEventListener('click', function (e) {
        if (e.target === sizeGuideOverlay) closeSizeGuide();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeSizeGuide();
      });
      if (sgTabs) sgTabs.querySelectorAll('[data-aqs-sizeguide-view]').forEach(function (btn) {
        btn.addEventListener('click', function () { sgSetView(btn.getAttribute('data-aqs-sizeguide-view')); });
      });
      sizeGuideOverlay.querySelectorAll('[data-aqs-sizeguide-unit]').forEach(function (btn) {
        btn.addEventListener('click', function () { sgSetUnit(btn.getAttribute('data-aqs-sizeguide-unit')); });
      });
      if (sgWaist) sgWaist.addEventListener('input', function () {
        if (sgCalcBtn) sgCalcBtn.disabled = !sgWaist.value.trim() && !(sgHip && sgHip.value.trim());
      });
      if (sgHip) sgHip.addEventListener('input', function () {
        if (sgCalcBtn) sgCalcBtn.disabled = !sgHip.value.trim() && !(sgWaist && sgWaist.value.trim());
      });
      if (sgCalcBtn) sgCalcBtn.addEventListener('click', sgCalculate);
      if (sgResetBtn) sgResetBtn.addEventListener('click', sgReset);
      if (sgBackBtn) sgBackBtn.addEventListener('click', function () { sgSetView('calculator'); });
      if (sgSelectBtn) sgSelectBtn.addEventListener('click', function () { sgApplySelection(); closeSizeGuide(); });
    }

    /* ── 3. Complete the Set / Mix & Match builder ── */
    var builderRoot = section.querySelector('.aqs-builder[data-ga-promo-id]');
    if (!builderRoot) return;

    var promoId = builderRoot.getAttribute('data-ga-promo-id');
    var promoName = builderRoot.getAttribute('data-ga-promo-name') || 'Complete the Set';

    var dataScript = document.getElementById(builderRoot.getAttribute('data-items-script'));
    var items = [];
    try {
      items = dataScript ? JSON.parse(dataScript.textContent) : [];
    } catch (err) {
      items = [];
    }

    var tiers = [];
    try {
      tiers = JSON.parse(builderRoot.getAttribute('data-tiers') || '[]');
    } catch (err2) {
      tiers = [];
    }
    var currencySymbol = builderRoot.getAttribute('data-currency-symbol') || '$';

    // Cart-linked tier progress: the checkout discount is a percentage off the
    // WHOLE cart once its subtotal crosses a spend tier, so tier progress must
    // project cart subtotal + selection — the selection alone under-reports a
    // shopper who already has items in cart. SSR seeds the value (minor units);
    // cart:change keeps it fresh. Thresholds and the cart subtotal are both in
    // the page's presentment currency (GBP on this instance) by design.
    var cartSubtotal = (parseInt(builderRoot.getAttribute('data-cart-subtotal'), 10) || 0) / 100;

    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart && typeof (cart.original_total_price || cart.total_price) === 'number') {
        cartSubtotal = (cart.original_total_price || cart.total_price) / 100;
        recalc();
        return;
      }
      // detail-less broadcast (defensive): fall back to fetching the cart
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        cartSubtotal = (c.original_total_price || c.total_price || 0) / 100;
        recalc();
      }).catch(function () { /* keep last known subtotal */ });
    });


    var tabButtons = builderRoot.querySelectorAll('[data-aqs-tab]');
    var itemRows = builderRoot.querySelectorAll('[data-aqs-item]');
    var fillEl = builderRoot.querySelector('[data-aqs-tier-fill]');
    var markerEls = builderRoot.querySelectorAll('[data-aqs-tier-marker]');
    var labelEls = builderRoot.querySelectorAll('[data-aqs-tier-label]');
    var subtotalEl = builderRoot.querySelector('[data-aqs-subtotal]');
    var finalEl = builderRoot.querySelector('[data-aqs-final]');
    var saveMsgEl = builderRoot.querySelector('[data-aqs-save-msg]');
    var addBtn = builderRoot.querySelector('[data-aqs-add]');
    var statusEl = builderRoot.querySelector('[data-aqs-status]');

    // Two tabs only: "complete" (all 5 checked) and "custom" (Mix & Match —
    // pick any subset). There is no Group A/B split here — every item is
    // always visible in both tabs; only the checked state differs.
    var activeTab = 'complete';

    // Liquid emits "key" as a bare JSON number ({{ product.id | json }}), but
    // row.getAttribute() always returns a string — compare as strings (see
    // yacht-fleet-drop.js's findItemByKey for the bug this avoids).
    function findItemByKey(key) {
      var keyStr = String(key);
      return items.filter(function (i) { return String(i.key) === keyStr; })[0];
    }

    function getTabLabel(tab) {
      var btn = builderRoot.querySelector('[data-aqs-tab="' + tab + '"]');
      return btn ? btn.textContent.trim() : 'Complete the Set';
    }

    function applyPreset(tab) {
      activeTab = tab;
      tabButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-aqs-tab') === tab ? 'true' : 'false');
      });
      itemRows.forEach(function (row) {
        var checkbox = row.querySelector('input[type="checkbox"]');
        if (checkbox && tab === 'complete') checkbox.checked = !checkbox.disabled;
        row.setAttribute('data-selected', checkbox && checkbox.checked ? 'true' : 'false');
      });
      recalc();
    }

    function selectedItems() {
      var out = [];
      itemRows.forEach(function (row) {
        var checkbox = row.querySelector('input[type="checkbox"]');
        if (!checkbox || !checkbox.checked) return;
        var key = row.getAttribute('data-aqs-item');
        var it = findItemByKey(key);
        if (it) out.push(it);
      });
      return out;
    }

    function selectedVariantId(key) {
      var row = builderRoot.querySelector('[data-aqs-item="' + key + '"]');
      var sel = row ? row.querySelector('[data-aqs-item-size]') : null;
      return sel ? sel.value : null;
    }

    function money(n) {
      return currencySymbol + n.toFixed(2);
    }

    function recalc() {
      var sel = selectedItems();
      var subtotal = sel.reduce(function (sum, it) { return sum + (it.price || 0); }, 0);

      // Tiers are judged on the PROJECTED cart (what's already in cart + this
      // selection) — the same number checkout's discount will see.
      var projected = cartSubtotal + subtotal;

      var reached = null;
      var next = null;
      for (var i = 0; i < tiers.length; i++) {
        if (projected >= tiers[i].threshold) reached = tiers[i];
        else { next = tiers[i]; break; }
      }

      var topThreshold = tiers.length ? tiers[tiers.length - 1].threshold : 1;
      var fillPct = Math.min(100, (projected / topThreshold) * 100);
      if (fillEl) fillEl.style.width = fillPct + '%';

      markerEls.forEach(function (m, i) {
        m.setAttribute('data-reached', tiers[i] && projected >= tiers[i].threshold ? 'true' : 'false');
      });
      labelEls.forEach(function (l, i) {
        l.setAttribute('data-reached', tiers[i] && projected >= tiers[i].threshold ? 'true' : 'false');
      });

      if (subtotalEl) subtotalEl.textContent = money(subtotal);

      // The struck price / final price stay scoped to THIS selection (that's
      // what the button adds); only the tier percentage comes from the
      // projected cart, mirroring how checkout discounts these items.
      if (reached && sel.length) {
        var discounted = subtotal * (1 - reached.percent / 100);
        if (finalEl) finalEl.textContent = money(discounted);
        if (subtotalEl) subtotalEl.classList.add('aqs-builder__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.remove('aqs-builder__save-msg--pending');
          saveMsgEl.textContent = reached.percent + '% OFF unlocked — you save ' + money(subtotal - discounted) + ' on these items!';
        }
      } else if (reached) {
        // Cart alone already crossed a tier, nothing selected here yet.
        if (finalEl) finalEl.textContent = money(subtotal);
        if (subtotalEl) subtotalEl.classList.remove('aqs-builder__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.remove('aqs-builder__save-msg--pending');
          saveMsgEl.textContent = reached.percent + '% OFF unlocked — anything you add gets ' + reached.percent + '% off too';
        }
      } else {
        if (finalEl) finalEl.textContent = money(subtotal);
        if (subtotalEl) subtotalEl.classList.remove('aqs-builder__subtotal--struck');
        if (saveMsgEl) {
          saveMsgEl.classList.add('aqs-builder__save-msg--pending');
          if (next) {
            var remaining = next.threshold - projected;
            saveMsgEl.textContent = 'Add ' + money(remaining) + ' more to unlock ' + next.percent + '% off';
          } else {
            saveMsgEl.textContent = 'Add items to unlock a discount';
          }
        }
      }

      if (addBtn) addBtn.disabled = sel.length === 0;
    }

    itemRows.forEach(function (row) {
      var checkbox = row.querySelector('input[type="checkbox"]');
      if (!checkbox) return;
      row.addEventListener('click', function (e) {
        if (e.target === checkbox) return; // avoid double-toggle
        if (e.target.closest('select')) return; // picking a size shouldn't (un)check the item
        if (e.target.closest('a')) return; // size-guide link inside a row shouldn't toggle it
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change'));
      });
      checkbox.addEventListener('change', function () {
        // Any manual (un)check moves to Mix & Match — same convention as
        // yacht-fleet-drop.js's bundle builder.
        applyPreset('custom');
      });

      var itemSizeSelect = row.querySelector('[data-aqs-item-size]');
      if (itemSizeSelect) {
        itemSizeSelect.addEventListener('click', function (e) { e.stopPropagation(); });
        itemSizeSelect.addEventListener('change', function (e) { e.stopPropagation(); });
      }
    });

    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        applyPreset(btn.getAttribute('data-aqs-tab'));
      });
    });

    // Impression: builder is a *promotion* surface, not a list.
    observeOnce(builderRoot, sectionId + ':builder', seen, function () {
      var sel = selectedItems();
      var payloadItems = (sel.length ? sel : items).map(function (it, i) {
        var variantId = selectedVariantId(it.key) || (it.variants[0] || {}).id || '';
        return {
          item_id: itemId(it.productId, variantId),
          item_name: it.name,
          price: it.price,
          index: i + 1,
          promotion_id: promoId,
          promotion_name: promoName,
        };
      });
      gaPush('ee_view_promotion', {
        promotion_id: promoId,
        promotion_name: promoName,
        creative_name: getTabLabel(activeTab),
        creative_slot: 'complete_the_set',
        items: payloadItems,
      });
    });

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var sel = selectedItems();
        if (!sel.length) return;

        var lineItems = [];
        sel.forEach(function (it) {
          var variantId = selectedVariantId(it.key);
          if (!variantId) return;
          lineItems.push({ id: parseInt(variantId, 10), quantity: 1 });
        });

        if (!lineItems.length) {
          if (statusEl) {
            statusEl.textContent = 'Could not find available sizes for the selected items.';
            statusEl.className = 'aqs-builder__status aqs-builder__status--error';
          }
          return;
        }

        addBtn.disabled = true;
        if (statusEl) {
          statusEl.textContent = 'Adding to cart…';
          statusEl.className = 'aqs-builder__status';
        }

        function onSuccess() {
          addBtn.disabled = false;
          if (statusEl) {
            statusEl.textContent = 'Added ' + lineItems.length + ' item' + (lineItems.length === 1 ? '' : 's') + ' to your cart.';
            statusEl.className = 'aqs-builder__status aqs-builder__status--ok';
          }

          gaPush('ee_select_promotion', {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: getTabLabel(activeTab),
            creative_slot: 'complete_the_set',
            items: sel.map(function (it, i) {
              return {
                item_id: itemId(it.productId, selectedVariantId(it.key) || ''),
                item_name: it.name,
                price: it.price,
                index: i + 1,
                promotion_id: promoId,
                promotion_name: promoName,
              };
            }),
          });
        }

        function onError() {
          addBtn.disabled = false;
          if (statusEl) {
            statusEl.textContent = 'Something went wrong adding to your cart — please try again.';
            statusEl.className = 'aqs-builder__status aqs-builder__status--error';
          }
        }

        whenS1Ready('addToCartAndRefreshCart', function (s1) {
          var result = s1.addToCartAndRefreshCart(lineItems);
          if (result && typeof result.then === 'function') {
            result.then(onSuccess).catch(onError);
          } else {
            onSuccess();
          }
        }, function () {
          onError(new Error('_s1.addToCartAndRefreshCart never became available'));
        });
      });
    }

    applyPreset(activeTab);
  });
})();
